import { TimeSlotType, TimeSpan } from 'fitbeat.models';
import { AllBookingTimeFrame } from '../entities';
import { Day, TimeType } from '../helpers';
import { WorkoutPausesConfig } from './workoutpausesconfig';
import { WorkoutType } from './workouttype';

export enum DaysOfTheWeek {
  Sunday = 0,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

let DEMERITS_ENABLED = false;

export interface IBookingTimeFrame {
  timeFrame: AllBookingTimeFrame;
  workoutType: number;
  bookingIntervalInMinutes: number;
  /**
   * Whether a configured time frame allows _scheduled bookings_ if not overridden in the gym's
   * `IPersistedGym.overridenAllowedBookingTimeFrames`
   * This defaults to `true` in AllowedBookingTimeFrames if not set in the configured time frame
   */
  allowBookingsByDefault?: boolean;
  /**
   * Whether a configured time frame allows _on demand bookings_ if not overridden in the gym's
   * `IPersistedGym.overridenAllowedBookingTimeFrames`
   * This defaults to `true` in AllowedBookingTimeFrames if not set in the configured time frame
   */
  allowOnDemandByDefault?: boolean;
}

export class BookingsConfig {
  public static readonly bookingIntervalsInMinutes = 30;

  public static readonly longBookingIntervalsInMinutes = 60;

  public static readonly homeBookingIntervalsInMinutes = 15;

  public static readonly provisionalBookingExpiryInHours = 6;

  public static readonly maxNumberOfBookingInvitesForUser = 5;

  public static readonly maxNumberOfMinutesPriorToExerciseForCheckin = 20;

  public static readonly maxNumberOfMinutesAfterExerciseForCheckin = 10;

  public static readonly daysThatDemeritsAreConsideredFor = 7;

  public static readonly maxNumberOfDaysThatDemerittedUserCanGetAvailabilityFor = 5;

  public static readonly staticCardioBookingIntervalsInMinutes = 12;

  public static readonly minNumberOfCharactersForSearch = 3;

  public static readonly halfHourStartTimeDefaultStationOffSet = 2;

  public static readonly defaultStartingStation = 0;

  public static readonly earliestStartingMinuteForBookingsOnHalfHour = 28;

  // TODO: used for slot loading only, use for bookings as well
  public static readonly minutesGraceTimeForStaticBookingsAfterClassStarted = 1;

  public static readonly minutesGraceTimeForBookingsAfterClassStarted = 10;

  public static readonly minutesGraceTimeForBookingsAfterClassStartedWhenEvenLaterLateBookingsEnabled = 28;

  public static readonly maxNumberOfAllowedHomeBookingsForDay = 10;

  public static readonly minutesFromNowHomeBookingSlot = 2;

  public static readonly minutesFromHomeTimeIntervalToNotAllowNowSlot = 3;

  public static readonly allowedStartTimeBeforeFirstExercise = 10;

  public static readonly homeBookingAvailableSlotsStartTimeHour = 1;

  public static readonly homeBookingBufferSecondsBeforeStartOfWarmup = 4;

  public static readonly streamingCallChatTimeBeforeStartOfWarmup = 300;

  public static getCircuitBookingTimeFrames(): IBookingTimeFrame[] {
    return [
      {
        timeFrame: '30min',
        workoutType: WorkoutType.MediumStudioWorkout,
        bookingIntervalInMinutes: BookingsConfig.bookingIntervalsInMinutes,
      },
      {
        timeFrame: 'hour',
        workoutType: WorkoutType.FullStudioWorkout,
        bookingIntervalInMinutes: BookingsConfig.longBookingIntervalsInMinutes,
      },
    ];
  }

  public static getStaticFunctionalBookingTimeFrames(): IBookingTimeFrame[] {
    return [
      {
        timeFrame: '15min',
        workoutType: WorkoutType.QuickStaticFunctionalWorkout,
        bookingIntervalInMinutes: BookingsConfig.homeBookingIntervalsInMinutes,
      },
      {
        timeFrame: '30min',
        workoutType: WorkoutType.MediumStudioWorkout,
        bookingIntervalInMinutes: BookingsConfig.bookingIntervalsInMinutes,
      },
      {
        timeFrame: 'hour',
        workoutType: WorkoutType.FullStudioWorkout,
        bookingIntervalInMinutes: BookingsConfig.longBookingIntervalsInMinutes,
      },
    ];
  }

  public static getStaticMachineBookingTimeFrames(): IBookingTimeFrame[] {
    const bookingIntervalInMinutes = BookingsConfig.staticCardioBookingIntervalsInMinutes;

    return [
      {
        timeFrame: '10min',
        workoutType: WorkoutType.QuickHomeOrStaticCardioWorkout,
        bookingIntervalInMinutes,
      },
      {
        timeFrame: '20min',
        workoutType: WorkoutType.MediumHomeOrStaticCardioWorkout,
        bookingIntervalInMinutes,
      },
      {
        timeFrame: '30min',
        workoutType: WorkoutType.FullHomeOrStaticCardioWorkout,
        bookingIntervalInMinutes,
      },
      {
        timeFrame: '40min',
        workoutType: WorkoutType.ExtraLongHomeOrStaticCardioWorkout,
        bookingIntervalInMinutes,
      },
      {
        timeFrame: '50min',
        workoutType: WorkoutType.FullStaticCardioWorkout,
        bookingIntervalInMinutes,
      },
    ];
  }

  public static generateDays(testingSet = false): Day[] {
    return [
      BookingsConfig.generateDay(DaysOfTheWeek.Sunday, testingSet),
      BookingsConfig.generateDay(DaysOfTheWeek.Monday, testingSet),
      BookingsConfig.generateDay(DaysOfTheWeek.Tuesday, testingSet),
      BookingsConfig.generateDay(DaysOfTheWeek.Wednesday, testingSet),
      BookingsConfig.generateDay(DaysOfTheWeek.Thursday, testingSet),
      BookingsConfig.generateDay(DaysOfTheWeek.Friday, testingSet),
      BookingsConfig.generateDay(DaysOfTheWeek.Saturday, testingSet),
    ];
  }

  public static generateAnytimeDays(): Day[] {
    const anyTimeDays = BookingsConfig.generateAnytimeDay();
    return [
      anyTimeDays,
      anyTimeDays,
      anyTimeDays,
      anyTimeDays,
      anyTimeDays,
      anyTimeDays,
      anyTimeDays,
    ];
  }

  public static generate24HourTimeSlots(): Day[] {
    const day = new Day([new TimeType(new TimeSpan('0000', '2359'), TimeSlotType.Peak)]);
    return [day, day, day, day, day, day, day];
  }

  public static getDemeritsEnabled(): boolean {
    return DEMERITS_ENABLED;
  }

  public static enableDemerits() {
    DEMERITS_ENABLED = true;
  }

  public static disableDemerits() {
    DEMERITS_ENABLED = false;
  }

  public static workoutTypeDuration(workoutType: WorkoutType): number {
    if (!WorkoutPausesConfig.isFirstStageAPause(workoutType)) {
      return (workoutType * 2);
    }

    return (workoutType * 2) - 2;
  }

  private static generateDay(day: DaysOfTheWeek, testingSet: boolean): Day {
    if (!testingSet) {
      // temporarily keep real gym open for 23 hours for testing
      if (day === DaysOfTheWeek.Wednesday) {
        return new Day(
          [
            new TimeType(
              new TimeSpan('0600', '1800'),
              TimeSlotType.OffPeak,
            ),
            new TimeType(
              new TimeSpan('1800', '1900'),
              TimeSlotType.Peak,
            ),
            new TimeType(
              new TimeSpan('1300', '2359'),
              TimeSlotType.OffPeak,
            ),
          ],
        );
      }
      if (day === DaysOfTheWeek.Thursday) {
        return new Day(
          [
            new TimeType(
              new TimeSpan('0600', '1800'),
              TimeSlotType.OffPeak,
            ),
            new TimeType(
              new TimeSpan('1800', '1900'),
              TimeSlotType.Peak,
            ),
            new TimeType(
              new TimeSpan('1300', '2359'),
              TimeSlotType.OffPeak,
            ),
          ],
        );
      }

      return new Day(
        [
          new TimeType(
            new TimeSpan('0500', '0700'),
            TimeSlotType.OffPeak,
          ),
          new TimeType(
            new TimeSpan('0700', '1700'),
            TimeSlotType.OffPeak,
          ),
          new TimeType(
            new TimeSpan('1700', '1800'),
            TimeSlotType.OffPeak,
          ),
          new TimeType(
            new TimeSpan('1800', '2359'),
            TimeSlotType.OffPeak,
          ),
        ],
      );
    }

    // testing set:
    switch (day) {
      case DaysOfTheWeek.Sunday:
        return new Day(
          [],
        );

      case DaysOfTheWeek.Saturday:
        return new Day(
          [
            new TimeType(
              new TimeSpan('0700', '1000'),
              TimeSlotType.Peak,
            ),
            new TimeType(
              new TimeSpan('1000', '1100'),
              TimeSlotType.OffPeak,
            ),
          ],
        );

      case DaysOfTheWeek.Tuesday:
        return new Day(
          [
            new TimeType(
              new TimeSpan('0600', '0900'),
              TimeSlotType.Peak,
            ),
            new TimeType(
              new TimeSpan('0900', '1000'),
              TimeSlotType.None,
            ),
            new TimeType(
              new TimeSpan('1000', '1200'),
              TimeSlotType.OffPeak,
            ),
            new TimeType(
              new TimeSpan('1200', '1330'),
              TimeSlotType.Peak,
            ),
            new TimeType(
              new TimeSpan('1330', '1400'),
              TimeSlotType.None,
            ),
            new TimeType(
              new TimeSpan('1400', '1700'),
              TimeSlotType.OffPeak,
            ),
            new TimeType(
              new TimeSpan('1700', '1900'),
              TimeSlotType.Peak,
            ),
          ],
        );

      default:
        return new Day(
          [
            new TimeType(
              new TimeSpan('1000', '1200'),
              TimeSlotType.OffPeak,
            ),
            new TimeType(
              new TimeSpan('1400', '1700'),
              TimeSlotType.OffPeak,
            ),
            new TimeType(
              new TimeSpan('0600', '1000'),
              TimeSlotType.Peak,
            ),
            new TimeType(
              new TimeSpan('1200', '1400'),
              TimeSlotType.Peak,
            ),
            new TimeType(
              new TimeSpan('1700', '1900'),
              TimeSlotType.Peak,
            ),
          ],

        );
    }
  }

  private static generateAnytimeDay(): Day {
    return new Day(
      [
        new TimeType(
          new TimeSpan('0100', '2300'),
          TimeSlotType.Peak,
        ),
      ],
    );
  }
}
